const MIN_WINDOW = 0.1;

interface TrimmableLayer {
  type: 'image' | 'video' | 'audio' | 'text' | 'motion';
  start: number;
  end: number;
  source?: string;
  speed?: number;
  trimStart?: number;
  trimEnd?: number;
  mediaDuration?: number;
}

/** Drag a lane edge. Image/text: resize the window. Video/audio: trim the
 *  media in/out point — the window length tracks (trimEnd − trimStart) / speed
 *  and can never exceed the available media. Clip-mirror video layers
 *  (`source === 'clip'`) have no independent media to trim, so they behave
 *  like image/text: only the window resizes. */
export function applyLaneTrim(
  { layer, edge, deltaSec, clipDuration }:
  { layer: TrimmableLayer; edge: 'start' | 'end'; deltaSec: number; clipDuration: number },
): Partial<{ start: number; end: number; trimStart: number; trimEnd: number }> {
  const isMedia = (layer.type === 'video' && layer.source !== 'clip') || layer.type === 'audio';
  if (!isMedia) {
    if (edge === 'end') {
      return { end: Math.min(Math.max(layer.end + deltaSec, layer.start + MIN_WINDOW), clipDuration) };
    }
    return { start: Math.max(0, Math.min(layer.start + deltaSec, layer.end - MIN_WINDOW)) };
  }

  const speed = layer.speed ?? 1;
  const trimStart = layer.trimStart ?? 0;
  const trimEnd = layer.trimEnd ?? layer.mediaDuration ?? layer.end - layer.start;
  const mediaDuration = layer.mediaDuration ?? trimEnd;

  if (edge === 'end') {
    const maxEnd = Math.min(clipDuration, layer.start + (mediaDuration - trimStart) / speed);
    const end = Math.min(Math.max(layer.end + deltaSec, layer.start + MIN_WINDOW), maxEnd);
    return { end, trimEnd: trimStart + (end - layer.start) * speed };
  }

  const minStart = Math.max(0, layer.start - trimStart / speed);
  const start = Math.max(minStart, Math.min(layer.start + deltaSec, layer.end - MIN_WINDOW));
  return { start, trimStart: trimStart + (start - layer.start) * speed };
}
